import React, { useMemo } from "react";

interface ExecutionDevice {
  id: string;
  serial: string;
  model: string;
}

interface ExecutionLite {
  id: string;
  scenarioName: string;
  status: string;
  durationMs: number | null;
  createdAt?: string;
  device?: ExecutionDevice | null;
}

/**
 * Horizontal-bars-per-device timeline. Each scenario is a colored block placed
 * by start time (createdAt - durationMs is best-effort start-of-execution).
 *
 * Idle gaps between blocks are visible. Failed scenarios are red — patterns
 * like "this device started failing midway through" jump out at a glance.
 */
export default function RunGantt({
  executions,
}: {
  executions: ExecutionLite[];
}) {
  const data = useMemo(() => {
    if (executions.length === 0) return null;

    // Compute per-execution [startMs, endMs] from createdAt + durationMs.
    // createdAt is the row-creation timestamp, which our hooks emit at the
    // end of the cucumber After hook — so end ≈ createdAt, start = end - dur.
    const items: Array<{
      execId: string;
      serial: string;
      model: string;
      scenario: string;
      status: string;
      startMs: number;
      endMs: number;
    }> = [];

    for (const e of executions) {
      if (!e.device || !e.createdAt) continue;
      const endMs = new Date(e.createdAt).getTime();
      const dur = e.durationMs ?? 0;
      const startMs = endMs - dur;
      items.push({
        execId: e.id,
        serial: e.device.serial,
        model: e.device.model,
        scenario: e.scenarioName,
        status: e.status,
        startMs,
        endMs,
      });
    }

    if (items.length === 0) return null;

    const minMs = items.reduce((m, x) => Math.min(m, x.startMs), Infinity);
    const maxMs = items.reduce((m, x) => Math.max(m, x.endMs), -Infinity);
    const totalMs = Math.max(1, maxMs - minMs);

    const byDevice = new Map<
      string,
      { serial: string; model: string; items: typeof items }
    >();
    for (const it of items) {
      const cur = byDevice.get(it.serial) ?? {
        serial: it.serial,
        model: it.model,
        items: [],
      };
      cur.items.push(it);
      byDevice.set(it.serial, cur);
    }

    const lanes = Array.from(byDevice.values()).sort((a, b) =>
      a.serial.localeCompare(b.serial)
    );

    return { lanes, minMs, maxMs, totalMs };
  }, [executions]);

  if (!data) return null;

  const colorFor = (status: string) => {
    if (status === "passed") return "var(--color-success)";
    if (status === "failed") return "var(--color-danger)";
    if (status === "skipped") return "var(--color-warning)";
    return "var(--color-text-secondary)";
  };

  const totalSec = Math.round(data.totalMs / 1000);
  const tickCount = 5;

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-md)",
        marginBottom: "var(--space-md)",
      }}
    >
      <div
        style={{
          fontSize: "var(--font-size-xs)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "var(--color-text-secondary)",
          marginBottom: 8,
        }}
      >
        Timeline — {data.lanes.length} device(s) · {totalSec}s span
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.lanes.map((lane) => (
          <div
            key={lane.serial}
            style={{
              display: "grid",
              gridTemplateColumns: "180px 1fr",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-secondary)",
                fontFamily: "var(--font-mono, monospace)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {lane.model} ({lane.serial.slice(0, 8)})
            </div>
            <div
              style={{
                position: "relative",
                height: 16,
                background: "var(--color-foreground)",
                borderRadius: 3,
              }}
            >
              {lane.items.map((it) => {
                const leftPct = ((it.startMs - data.minMs) / data.totalMs) * 100;
                const widthPct = Math.max(
                  0.2,
                  ((it.endMs - it.startMs) / data.totalMs) * 100
                );
                return (
                  <div
                    key={it.execId}
                    title={`${it.scenario} — ${it.status} · ${
                      ((it.endMs - it.startMs) / 1000).toFixed(1)
                    }s`}
                    style={{
                      position: "absolute",
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      top: 1,
                      bottom: 1,
                      background: colorFor(it.status),
                      borderRadius: 2,
                      opacity: 0.9,
                    }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* tick row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "180px 1fr",
          gap: 8,
          marginTop: 4,
        }}
      >
        <div />
        <div
          style={{
            position: "relative",
            height: 12,
            fontSize: 10,
            color: "var(--color-text-secondary)",
          }}
        >
          {Array.from({ length: tickCount + 1 }).map((_, i) => {
            const pct = (i / tickCount) * 100;
            const sec = Math.round((i / tickCount) * totalSec);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: `${pct}%`,
                  transform: "translateX(-50%)",
                }}
              >
                {sec}s
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
